import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import BodyCard from "../../../../components/fundamentals/body-card/body-card";
import Breadcrumb from "../../../../components/fundamentals/breadcrumb/Breadcrumb";
import Button from "../../../../components/fundamentals/button";
import InputField from "../../../../components/form/input";
import useNotification from "../../../../hooks/use-notification";
import useImperativeDialog from "../../../../hooks/use-imperative-dialog";
import authService from "../../../../services/auth.service";
import plateformeSettingsService from "../../../../services/plateforme-settings.service";

type FormValues = { coefficient: string };

/**
 * RAG-65 — Réglage du coefficient de prix de vente des produits de la Plateforme Socoda.
 *
 * Prix de vente affiché = prix d'achat communiqué par la plateforme × coefficient.
 * La modification s'applique immédiatement à tout le catalogue plateforme.
 * L'écriture est réservée aux profils « administrateur » (pas « lecture seule ») :
 * l'API refuse de toute façon (403), le formulaire se verrouille côté écran.
 */
export default function PlateformeSettingsPage() {
  const notify = useNotification();
  const confirm = useImperativeDialog();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canWrite, setCanWrite] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { coefficient: "1" } });

  useEffect(() => {
    let active = true;

    Promise.all([
      plateformeSettingsService.getCoefficient(),
      authService
        .me()
        .then((me) => me.role)
        .catch(() => null),
    ])
      .then(([coefficient, role]) => {
        if (!active) return;
        reset({ coefficient: String(coefficient) });
        setCanWrite(role === "super_admin" || role === "admin");
      })
      .catch(() => {
        if (active) notify("Erreur", "Impossible de charger le coefficient.", "error");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reset, notify]);

  const onSubmit = async (values: FormValues) => {
    const coefficient = parseFloat(String(values.coefficient).replace(",", "."));
    if (!isFinite(coefficient) || coefficient <= 0) {
      notify("Erreur", "Veuillez saisir un nombre strictement positif (ex. 1.10).", "error");
      return;
    }

    const confirmed = await confirm({
      heading: "Modifier le coefficient",
      text: `Appliquer le coefficient ${coefficient} à tous les produits de la plateforme Socoda ? Les prix de vente seront recalculés immédiatement.`,
      confirmText: "Oui, enregistrer",
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const saved = await plateformeSettingsService.updateCoefficient(coefficient);
      reset({ coefficient: String(saved) });
      notify("Enregistré", `Coefficient ${saved} appliqué aux produits de la plateforme.`, "success");
    } catch (e: any) {
      notify("Erreur", e?.message ?? "Enregistrement impossible.", e?.status === 403 ? "warning" : "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col grow h-full">
      <div className="w-full flex flex-col grow">
        <Breadcrumb
          previousRoute="/settings"
          previousBreadcrumb="Paramètres"
          currentPage={"Plateforme Socoda"}
        />
        <BodyCard
          title="Coefficient de prix — Plateforme Socoda"
          subtitle="Prix de vente affiché = prix d'achat de la plateforme × coefficient. Appliqué à tous les produits de la plateforme."
          className="h-fit"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-base max-w-sm mt-base">
            <InputField
              label="Coefficient"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="Ex. 1.10"
              errors={errors}
              disabled={loading || !canWrite}
              {...register("coefficient", {
                required: "Le coefficient est obligatoire.",
              })}
            />
            {!loading && !canWrite && (
              <span className="inter-small-regular text-grey-50">
                Votre profil est en lecture seule : vous ne pouvez pas modifier ce réglage.
              </span>
            )}
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="small"
                type="submit"
                loading={saving}
                disabled={loading || saving || !canWrite}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        </BodyCard>
      </div>
    </div>
  );
}
